import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { DateUtils } from '@utils/DateUtils';
import { FormValidators } from '@validators/form.validators';

@Injectable({
    providedIn: 'root'
})
export class FileService {

    // constructor() { }

    /**
     * Verifica se um valor de data lido de um ficheiro importado é válido.
     * Aceita datas no formato pt (dd/mm/aaaa ou dd-mm-aaaa) e qualquer formato reconhecido nativamente pelo Date.
     * Não aceitar apenas via `new Date()` porque este assume MM/DD/AAAA para datas com "/", rejeitando datas pt válidas (ex.: 22/02/1978).
     * @param value valor não vazio (número de série do Excel, string ou Date)
     */
    private static IsValidImportDate(value: any): boolean {

        if (typeof value === 'number') return true; // data lida como número de série do Excel

        const match = String(value).trim().match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);

        if (match) {
            const day = Number(match[1]), month = Number(match[2]), year = Number(match[3]);
            const date = new Date(year, month - 1, day);

            return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
        }

        return !isNaN(new Date(value).getTime());
    }

    /**
     * Agrupa os números das linhas de dados (0-based, na matriz original) por combinação de valores exatamente
     * iguais (após trim) numa ou mais colunas, considerando só as combinações repetidas.
     * Base comum para deteção de nomes/NIFs duplicados (ex.: mesma NIF só é duplicado se a morada também repetir).
     * Linhas com alguma das colunas vazia são ignoradas (nada a comparar).
     * @param data dados lidos via ReadExcelFile
     * @param cols índice (0-based) da coluna a comparar, ou lista de colunas para chave composta
     * @param startRow primeira linha (0-based) de dados na matriz original
     */
    private static FindDuplicateRows(data: any[][], cols: number | number[], startRow: number): { values: string[]; rows: number[] }[] {

        const colList = Array.isArray(cols) ? cols : [cols];
        const rowsByKey = new Map<string, { values: string[]; rows: number[] }>();

        for (let row = startRow; row < data.length; row++) {

            const dataRow = data[row] || [];
            const values = colList.map(col => String(dataRow[col] ?? '').trim());

            if (values.some(value => value === '')) continue;

            const key = values.join('||');

            if (!rowsByKey.has(key)) rowsByKey.set(key, { values, rows: [] });

            rowsByKey.get(key)!.rows.push(row);
        }

        return Array.from(rowsByKey.values()).filter(it => it.rows.length > 1);
    }

    public ExportAsExcelFile<T>(headers: string[], data: string[][], fileName: string): void {

        if (!data || data.length === 0) {
            console.warn('Não há dados para exportar.');
            return;
        }

        // Extrair dinamicamente os headers das chaves do primeiro objeto
        // const headers = Object.keys(data[0]);

        // Excel/xlsx impõe limite de 32767 caracteres por célula
        const EXCEL_CELL_MAX_LENGTH = 32767;

        const sanitizedData = data.map(row => row.map(cell =>
            typeof cell === 'string' && cell.length > EXCEL_CELL_MAX_LENGTH
                ? cell.substring(0, EXCEL_CELL_MAX_LENGTH)
                : cell
        ));

        // Converter dados para matriz [ [header1, header2], [linha1col1, linha1col2], ... ]
        const worksheetData = [
            headers, // primeira linha com headers
            ...sanitizedData
        ];

        // Criar worksheet
        const worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(worksheetData);

        // Criar workbook
        const workbook: XLSX.WorkBook = {
            Sheets: { 'Dados': worksheet },
            SheetNames: ['Dados']
        };

        // Gerar buffer binário
        const excelBuffer: any = XLSX.write(workbook, {
            bookType: 'xlsx',
            type: 'array'
        });

        // Salvar ficheiro
        this.saveAsExcelFile(excelBuffer, fileName);
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {

        const data: Blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
        });

        saveAs(data, `${fileName}_${DateUtils.FormatDate(new Date().toISOString()).replace(/\//g, '-')}.xlsx`);
    }

    /**
     * 
     * @param file 
     * @returns 
     */
    public ReadExcelFile(file: File): Promise<any[][]> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                try {
                    const workbook = XLSX.read(e.target.result, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const jsonData: any = XLSX.utils.sheet_to_json(worksheet, { header: 1 }); // array de arrays
                    resolve(jsonData);
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = (error) => reject(error);
            reader.readAsArrayBuffer(file);
        });
    }

    /**
     * Verifica se o Excel tem o formato esperado.
     * Parâmetro: dados lidos via XLSX.utils.sheet_to_json(worksheet, { header: 1 })
     * Retorno: objeto com {
     *   isValid: boolean,
     *   errors: string[]
     * }
     */
    public ValidateStructureForMerchantFile(data: any[][]): { isValid: boolean; errors: string[] } {
        debugger

        const errors: string[] = [];
        if (!Array.isArray(data) || data.length < 2) {
            return { isValid: false, errors: ['Faltam pelo menos 2 linhas (cabeçalhos)'] };
        }

        // const headerRow0 = data[0]; // Primeira linha: grupos
        const headerRow1 = data[1]; // Segunda linha: campos específicos

        // // Etapa 1: Verificar grupos esperados na linha 0
        // const expectedGroups = [
        //     'Informação Geral',
        //     'Localidade',
        //     'Contactos',
        //     'Dados do Responsável',
        //     'Observações'
        // ];

        // const actualGroups = this.extractGroups(headerRow0);
        // for (const group of expectedGroups) {
        //     if (!actualGroups.includes(group)) {
        //         errors.push(`Grupo ausente na linha 0: "${group}"`);
        //     }
        // }

        // Etapa 2: Verificar campos principais na linha 1 (subcabeçalhos)
        // Vamos criar um "mapa" dos campos esperados em cada bloco.
        // Exemplo: na linha 1, espera-se:
        // ['Nome', 'Descrição', 'NIF', 'IBAN', 'Nome Fiscal', 'Setor', 'Rua', 'Localidade', ...]
        const expectedFields = [
            'Nome', 'Descrição', 'NIF', 'IBAN', 'Nome Fiscal', 'Setor',
            'Rua', 'Localidade', 'Código Postal', 'Concelho', 'Distrito', 'Latitude', 'Longitude',
            'Telemóvel', 'Telefone', 'Email',
            '', // atenção à quebra de linha
            'Nome', 'NIF', 'Telemóvel', 'Telefone', 'Email', 'Tipo documento', 'Número documento',
            'Validade documento', 'Data nascimento',
            'Observações'
        ];

        // Como os dados são coluna a coluna, vamos comparar elemento a elemento
        const minLen = Math.min(headerRow1.length, expectedFields.length);
        for (let i = 0; i < minLen; i++) {
            const cell = headerRow1[i];
            const expected = expectedFields[i];
            // Trata quebras de linha inconsistentes ou espaços em branco
            const normalizedCell = String(cell ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
            const normalizedExpected = expected.trim().replace(/\s+/g, ' ').toLowerCase();

            if (normalizedCell !== normalizedExpected && !(cell === null && expected.includes('\r\n'))) {
                // Se for null mas esperávamos quebra de linha (ex: CAE), permitimos uma margem de erro
                if (cell === null && expected.includes('\r\n')) {
                    continue;
                }
                errors.push(`Campo inesperado na coluna ${i + 1} (linha 2): esperado "${normalizedExpected}", encontrado "${normalizedCell}"`);
            }
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * 
     * @param data 
     * @returns 
     */
    public ValidateStructureForConsumerFile(data: any[][]): { isValid: boolean; errors: string[] } {

        const errors: string[] = [];
        if (!Array.isArray(data) || data.length < 2) {
            return { isValid: false, errors: ['Faltam pelo menos 2 linhas (cabeçalhos)'] };
        }

        // const headerRow0 = data[0]; // Primeira linha: grupos
        const headerRow1 = data[1]; // Segunda linha: campos específicos

        // // Etapa 1: Verificar grupos esperados na linha 0
        // const expectedGroups = [
        //     'Informação Geral',
        //     'Localidade',
        //     'Contactos',
        //     'Dados do Responsável',
        //     'Observações'
        // ];

        // const actualGroups = this.extractGroups(headerRow0);
        // for (const group of expectedGroups) {
        //     if (!actualGroups.includes(group)) {
        //         errors.push(`Grupo ausente na linha 0: "${group}"`);
        //     }
        // }

        // Etapa 2: Verificar campos principais na linha 1 (subcabeçalhos)
        // Vamos criar um "mapa" dos campos esperados em cada bloco.
        // Exemplo: na linha 1, espera-se:
        // ['Nome', 'Descrição', 'NIF', 'IBAN', 'Nome Fiscal', 'Setor', 'Rua', 'Localidade', ...]
        const expectedFields: (string | string[])[] = [
            "Name", "BirthDate", "Document Type", "IdentificationDocumentNumber", "Expire document date", "VatNumber", "SexGender", "Address", "Location", ["Postal code", "Postalcode"], "Phone", "Email", "Id_Externo"
        ];


        // Como os dados são coluna a coluna, vamos comparar elemento a elemento
        const minLen = Math.min(headerRow1.length, expectedFields.length);
        for (let i = 0; i < minLen; i++) {
            const cell = headerRow1[i];
            const expected = expectedFields[i];
            const expectedOptions = Array.isArray(expected) ? expected : [expected];
            // Trata quebras de linha inconsistentes ou espaços em branco
            const normalizedCell = String(cell ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
            const normalizedOptions = expectedOptions.map(it => it.trim().replace(/\s+/g, ' ').toLowerCase());

            if (!normalizedOptions.includes(normalizedCell) && !(cell === null && expectedOptions.some(it => it.includes('\r\n')))) {
                errors.push(`Campo inesperado na coluna ${i + 1} (linha 2): esperado "${normalizedOptions.join(' / ')}", encontrado "${normalizedCell}"`);
            }
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Valida o conteúdo (valores) das linhas de dados de um ficheiro de comerciantes, célula a célula.
     * Assume que a estrutura (cabeçalhos) já foi validada por ValidateStructureForMerchantFile.
     * Por agora só valida as colunas de data (opcionais): Validade documento e Data nascimento.
     * @param data dados lidos via ReadExcelFile (linha 0 e 1 são cabeçalho, dados começam na linha 2)
     * @returns lista de erros por posição (linha/coluna) na matriz original
     */
    public ValidateMerchantFileValues(data: any[][]): { row: number; col: number; message: string }[] {

        const errors: { row: number; col: number; message: string }[] = [];

        if (!Array.isArray(data) || data.length <= 2) return errors;

        const isEmpty = (value: any) => value === null || value === undefined || String(value).trim() === '';

        const isValidDate = (value: any) => isEmpty(value) ? false : FileService.IsValidImportDate(value);

        // Índices (0-based) alinhados com expectedFields em ValidateStructureForMerchantFile
        const dateColumns: { [col: number]: string } = {
            24: 'Validade do documento inválida', // Validade documento
            25: 'Data de nascimento inválida', // Data nascimento
        };

        for (let row = 2; row < data.length; row++) {

            const dataRow = data[row] || [];

            if (dataRow.every(cell => isEmpty(cell))) continue; // ignora linhas totalmente vazias

            for (const col of Object.keys(dateColumns).map(Number)) {

                const value = dataRow[col];

                if (!isEmpty(value) && !isValidDate(value)) errors.push({ row, col, message: dateColumns[col] });
            }
        }

        // Um NIF pode aparecer em mais que uma linha (ex.: várias lojas da mesma empresa), desde que a morada seja diferente.
        // NIF + Morada repetidos na mesma linha é duplicação/erro: bloqueia o carregamento.
        for (const dup of FileService.FindDuplicateRows(data, [2, 6], 2))
            for (const row of dup.rows) errors.push({ row, col: 2, message: `NIF duplicado no ficheiro para a mesma morada (${dup.rows.length}x: "${dup.values[0]}")` });

        return errors;
    }

    /**
     * Valida o conteúdo (valores) das linhas de dados de um ficheiro de consumidores, célula a célula.
     * Assume que a estrutura (cabeçalhos) já foi validada por ValidateStructureForConsumerFile.
     * @param data dados lidos via ReadExcelFile (linha 0 e 1 são cabeçalho, dados começam na linha 2)
     * @returns erros (bloqueiam o carregamento) e avisos (não bloqueiam) por posição (linha/coluna) na matriz original
     */
    public ValidateConsumerFileValues(data: any[][]): { errors: { row: number; col: number; message: string }[]; warnings: { row: number; col: number; message: string }[] } {

        const errors: { row: number; col: number; message: string }[] = [];

        if (!Array.isArray(data) || data.length <= 2) return { errors, warnings: [] };

        const asControl = (value: any) => ({ value, pristine: false, markAsTouched: () => { } } as any);

        const isEmpty = (value: any) => value === null || value === undefined || String(value).trim() === '';

        const isValidDate = (value: any) => isEmpty(value) ? false : FileService.IsValidImportDate(value);

        // Valores aceites (lista de dropdown do ficheiro template ImportConsumers.xlsx)
        const documentTypeOptions = ['cc', 'bi', 'passport'];
        const sexGenderOptions = ['undefined', 'male', 'female'];

        // Campos obrigatórios: Nome, Número de documento, Morada, Código Postal, NIF. Restantes são opcionais, mas se preenchidos têm de ser válidos.
        const columnValidators: ((value: any) => string | null)[] = [
            value => isEmpty(value) ? 'Nome é obrigatório' : null, // Name
            value => (!isEmpty(value) && !isValidDate(value)) ? 'Data de nascimento inválida' : null, // BirthDate
            value => (!isEmpty(value) && !documentTypeOptions.includes(String(value).trim().toLowerCase())) ? `Tipo de documento inválido (esperado: ${documentTypeOptions.join(', ')})` : null, // Document Type
            value => isEmpty(value) ? 'Número de documento é obrigatório' : null, // IdentificationDocumentNumber
            value => (!isEmpty(value) && !isValidDate(value)) ? 'Data de validade do documento inválida' : null, // Expire document date
            value => isEmpty(value) ? 'NIF é obrigatório' : (FormValidators.NIFValidator(asControl(String(value))) ? 'NIF inválido' : null), // VatNumber
            value => (!isEmpty(value) && !sexGenderOptions.includes(String(value).trim().toLowerCase())) ? `Género inválido (esperado: ${sexGenderOptions.join(', ')})` : null, // SexGender
            value => isEmpty(value) ? 'Morada é obrigatória' : null, // Address
            () => null, // Location (opcional, texto livre)
            value => isEmpty(value) ? 'Código postal é obrigatório' : (FormValidators.ZipCodeValidator(asControl(String(value))) ? 'Código postal inválido (0000-000)' : null), // Postal code
            value => (!isEmpty(value) && FormValidators.TelephoneValidator(asControl(String(value)))) ? 'Telefone inválido' : null, // Phone (opcional)
            value => (!isEmpty(value) && FormValidators.EmailValidator(asControl(String(value).trim()))) ? 'Email inválido' : null, // Email (opcional)
            () => null, // Id_Externo (opcional, texto livre)
        ];

        for (let row = 2; row < data.length; row++) {

            const dataRow = data[row] || [];

            if (dataRow.every(cell => isEmpty(cell))) continue; // ignora linhas totalmente vazias

            const minLen = Math.min(dataRow.length, columnValidators.length);

            for (let col = 0; col < minLen; col++) {

                const message = columnValidators[col](dataRow[col]);

                if (message) errors.push({ row, col, message });
            }
        }

        // NIF tem de ser único no ficheiro: duplicados bloqueiam o carregamento (erro)
        for (const dup of FileService.FindDuplicateRows(data, 5, 2))
            for (const row of dup.rows) errors.push({ row, col: 5, message: `NIF duplicado no ficheiro (${dup.rows.length}x: "${dup.values[0]}")` });

        // Nome repetido pode ser legítimo, mas também pode ser erro de preenchimento: apenas alerta (aviso)
        const warnings: { row: number; col: number; message: string }[] = [];

        for (const dup of FileService.FindDuplicateRows(data, 0, 2))
            for (const row of dup.rows) warnings.push({ row, col: 0, message: `Nome duplicado encontrado no ficheiro (${dup.rows.length}x: "${dup.values[0]}")` });

        return { errors, warnings };
    }

    /**
     *
     * @param data
     * @returns
     */
    public ValidateStructureForConsumerHouseHoldersFile(data: any[][]): { isValid: boolean; errors: string[] } {

        const errors: string[] = [];
        if (!Array.isArray(data) || data.length < 1) {
            return { isValid: false, errors: ['Faltam pelo menos 2 linhas (cabeçalhos)'] };
        }

        const headerRow0 = data[0]; // Segunda linha: campos específicos


        // Etapa 2: Verificar campos principais na linha 1 (subcabeçalhos)
        // Vamos criar um "mapa" dos campos esperados em cada bloco.
        // Exemplo: na linha 1, espera-se:
        // ['Nome', 'Descrição', 'NIF', 'IBAN', 'Nome Fiscal', 'Setor', 'Rua', 'Localidade', ...]
        const expectedFields: string[][] = [
            ["USER_ID", "ID_EXTERNO"],
            ["USER_VAT_NUMBER", "NIF_RESPONSAVEL"],
            ["NIF_ALUNO"],
            ["NOME_ALUNO", "ID_EXTERNO_ALUNO"],
            ["VALOR_APOIO"],
            ["FIM_VALIDADE", "FIM VALIDADE"]

            // "Id", "Nome", "Data de Nascimento", "Número do Cartão de Cidadão", "Validade do Cartão de Cidadão", "NIF", "Morada", "Localidade", "Código Postal", "Telemóvel", "Email", "Id", "Nome", "NIF"
        ];


        // Como os dados são coluna a coluna, vamos comparar elemento a elemento
        const minLen = Math.min(headerRow0.length, expectedFields.length);
        for (let i = 0; i < minLen; i++) {
            const cell = headerRow0[i];
            const expectedOptions = expectedFields[i];
            // Trata quebras de linha inconsistentes ou espaços em branco
            const normalizedCell = String(cell ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
            const normalizedOptions = expectedOptions.map(expected => expected.trim().replace(/\s+/g, ' ').toLowerCase());

            if (!normalizedOptions.includes(normalizedCell) && !(cell === null && normalizedOptions.some(expected => expected.includes('\r\n')))) {
                errors.push(`Campo inesperado na coluna ${i + 1} (linha 2): esperado "${normalizedOptions.join(' / ')}", encontrado "${normalizedCell}"`);
            }
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Valida o conteúdo (valores) das linhas de dados de um ficheiro de agregados familiares, célula a célula.
     * Assume que a estrutura (cabeçalho) já foi validada por ValidateStructureForConsumerHouseHoldersFile.
     * @param data dados lidos via ReadExcelFile (linha 0 é cabeçalho, dados começam na linha 1)
     * @returns erros (bloqueiam o carregamento) e avisos (não bloqueiam) por posição (linha/coluna) na matriz original
     */
    public ValidateConsumerHouseHoldersFileValues(data: any[][]): { errors: { row: number; col: number; message: string }[]; warnings: { row: number; col: number; message: string }[] } {

        const errors: { row: number; col: number; message: string }[] = [];

        if (!Array.isArray(data) || data.length <= 1) return { errors, warnings: [] };

        const asControl = (value: any) => ({ value, pristine: false, markAsTouched: () => { } } as any);

        const isEmpty = (value: any) => value === null || value === undefined || String(value).trim() === '';

        const isValidDate = (value: any) => isEmpty(value) ? false : FileService.IsValidImportDate(value);

        // Ids externos e validade são opcionais. NIFs e valor de apoio são obrigatórios.
        const columnValidators: ((value: any) => string | null)[] = [
            () => null, // USER_ID / ID_EXTERNO (opcional)
            value => isEmpty(value) ? 'NIF do responsável é obrigatório' : (FormValidators.NIFValidator(asControl(String(value))) ? 'NIF do responsável inválido' : null), // USER_VAT_NUMBER / NIF_RESPONSAVEL
            value => isEmpty(value) ? 'NIF do aluno é obrigatório' : (FormValidators.NIFValidator(asControl(String(value))) ? 'NIF do aluno inválido' : null), // NIF_ALUNO
            () => null, // NOME_ALUNO / ID_EXTERNO_ALUNO (opcional)
            value => isEmpty(value) ? 'Valor de apoio é obrigatório' : (FormValidators.ValidateCurrencyNumber(asControl(String(value))) ? 'Valor de apoio inválido' : null), // VALOR_APOIO
            value => (!isEmpty(value) && !isValidDate(value)) ? 'Data de fim de validade inválida' : null, // FIM_VALIDADE (opcional)
        ];

        for (let row = 1; row < data.length; row++) {

            const dataRow = data[row] || [];

            if (dataRow.every(cell => isEmpty(cell))) continue; // ignora linhas totalmente vazias

            const minLen = Math.min(dataRow.length, columnValidators.length);

            for (let col = 0; col < minLen; col++) {

                const message = columnValidators[col](dataRow[col]);

                if (message) errors.push({ row, col, message });
            }

            const nifResponsavel = dataRow[1];
            const nifAluno = dataRow[2];

            if (!isEmpty(nifResponsavel) && !isEmpty(nifAluno) && String(nifResponsavel).trim() === String(nifAluno).trim()) {
                errors.push({ row, col: 1, message: 'NIF do responsável não pode ser igual ao NIF do aluno' });
                errors.push({ row, col: 2, message: 'NIF do aluno não pode ser igual ao NIF do responsável' });
            }
        }

        // NIF do aluno tem de ser único no ficheiro: duplicados bloqueiam o carregamento (erro)
        for (const dup of FileService.FindDuplicateRows(data, 2, 1))
            for (const row of dup.rows) errors.push({ row, col: 2, message: `NIF do aluno duplicado no ficheiro (${dup.rows.length}x: "${dup.values[0]}")` });

        // Nome do aluno repetido pode ser legítimo (irmãos), mas também pode ser erro de preenchimento: apenas alerta (aviso)
        const warnings: { row: number; col: number; message: string }[] = [];

        for (const dup of FileService.FindDuplicateRows(data, 3, 1))
            for (const row of dup.rows) warnings.push({ row, col: 3, message: `Nome do aluno duplicado encontrado no ficheiro (${dup.rows.length}x: "${dup.values[0]}")` });

        return { errors, warnings };
    }

    /**
     *
     * @param data
     * @returns
     */
    public ValidateStructureForCampaignChargingFile(data: any[][]): { isValid: boolean; errors: string[] } {

        const errors: string[] = [];
        if (!Array.isArray(data) || data.length < 3) {
            return { isValid: false, errors: ['Faltam pelo menos 3 linhas (cabeçalhos)'] };
        }

        // normaliza removendo acentuação e caracteres não alfanuméricos, já que o mesmo campo aparece com grafias diferentes consoante a entidade
        const normalize = (value: any): string => String(value ?? '')
            .normalize('NFD')
            .replace(/[^a-zA-Z0-9 ]/g, '')
            .trim().replace(/\s+/g, ' ').toLowerCase();

        // NIF obrigatório na coluna B (índice 1), linha 3 — posição fixa, não sujeita ao realinhamento por colunas em branco
        const nifSynonyms = ['nif', 'contribuinte'];
        const nifCell = normalize((data[2] || [])[1]);
        if (!nifSynonyms.includes(nifCell)) {
            errors.push(`NIF deve estar na coluna B (linha 3): esperado um de [${nifSynonyms.join(', ')}], encontrado "${nifCell}"`);
        }

        // remove colunas em branco (ex.: coluna vazia inicial usada por alguns modelos exportados) para não desalinhar as restantes colunas
        const headerRow2 = (data[2] || []).filter(cell => cell !== null && cell !== undefined && String(cell).trim() !== '');

        // cada campo aceita sinónimos (nomenclatura varia consoante a entidade que gera o ficheiro) e a coluna do cartão aceita sufixo (ex.: "Nº Cartão do Munícipe")
        const expectedFields: { synonyms: string[]; prefixMatch?: boolean }[] = [
            { synonyms: nifSynonyms },
            { synonyms: ['referencia'] },
            { synonyms: ['montante'] },
            { synonyms: ['beneficiario'] },
            { synonyms: ['idexterno', 'id cmfonline'] },
            { synonyms: ['n cartao'], prefixMatch: true }
        ];

        if (headerRow2.length < expectedFields.length) {
            errors.push(`Faltam colunas: esperado ${expectedFields.length}, encontrado ${headerRow2.length}`);
        }

        const minLen = Math.min(headerRow2.length, expectedFields.length);
        for (let i = 0; i < minLen; i++) {
            const normalizedCell = normalize(headerRow2[i]);
            const field = expectedFields[i];

            const matches = field.synonyms.some(syn => field.prefixMatch ? normalizedCell.startsWith(syn) : normalizedCell === syn);

            if (!matches) {
                errors.push(`Campo inesperado na coluna ${i + 1} (linha 3): esperado um de [${field.synonyms.join(', ')}], encontrado "${normalizedCell}"`);
            }
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }
    /**
   * Dada uma linha (array), extrai os "grupos" não nulos e não vazios,
   * ignorando null/undefined/strings em branco.
   */
    private extractGroups(row: any[]): string[] {
        return row
            .filter(c => c !== null && typeof c === 'string' && c.trim() !== '')
            .map(c => c.trim());
    }


    /**
     * Lê um ficheiro XML e converte-o em JSON.
     * Usa DOMParser para parsear e converte recursivamente para objeto JS.
     */
    public ReadXMLFile(file: File): Promise<any> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                try {
                    const xmlContent = e.target.result;
                    const parser = new DOMParser();
                    const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
                    const json = this.xmlToJson(xmlDoc.documentElement);
                    resolve(json);
                } catch (error: any) {
                    reject(error);
                }
            };
            reader.onerror = (error) => reject(error);
            reader.readAsText(file);
        });
    }

    /**
     * Converte nó XML recursivamente para objeto JavaScript.
     */
    private xmlToJson(xml: Element): any {
        const obj: any = {};
        if (xml.attributes && xml.attributes.length > 0) {
            obj['@attributes'] = {};
            for (let i = 0; i < xml.attributes.length; i++) {
                const attr = xml.attributes[i];
                obj['@attributes'][attr.name] = attr.value;
            }
        }

        if (xml.children.length === 0 && xml.textContent?.trim() !== '') {
            return xml.textContent?.trim();
        }

        for (let i = 0; i < xml.children.length; i++) {
            const child = xml.children[i] as Element;
            const childName = child.localName || child.nodeName;
            const childObj = this.xmlToJson(child);

            if (obj[childName]) {
                if (!Array.isArray(obj[childName])) {
                    obj[childName] = [obj[childName]];
                }
                obj[childName].push(childObj);
            } else {
                obj[childName] = childObj;
            }
        }

        return obj;
    }
}
